import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProfile, useMyMemberships } from './useQueries';
import { FOCUSED_LEAGUE_ID, isLeagueActive } from '../data/leagues';

/**
 * Resolve the "current league world" when the URL doesn't carry one — used by the
 * account sub-pages, deep links, and the back-compat /standings|/friends|/profile
 * redirects. Preference order: the player's chosen default league, then the focused
 * competition, then any active membership, then their first membership. Returns null
 * when they haven't joined anything (callers send those players to the lobby).
 */
export function useActiveLeague(): { leagueId: string | null; loading: boolean } {
  const { uid } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile(uid);
  const { data: memberships, isLoading: membershipsLoading } = useMyMemberships(uid);

  const leagueId = useMemo<string | null>(() => {
    const joined = memberships ?? [];
    if (joined.length === 0) return null;
    const joinedHas = (id: string | undefined | null) => !!id && joined.some((m) => m.leagueId === id);
    if (joinedHas(profile?.defaultLeagueId)) return profile!.defaultLeagueId!;
    if (joinedHas(FOCUSED_LEAGUE_ID)) return FOCUSED_LEAGUE_ID;
    return (joined.find((m) => isLeagueActive(m.leagueId)) ?? joined[0]).leagueId;
  }, [memberships, profile?.defaultLeagueId]);

  return { leagueId, loading: profileLoading || membershipsLoading };
}
